import { Box, ButtonBase, Typography, IconButton, alpha } from "@mui/material";
import { Add, Remove, DeleteOutline, EditOutlined } from "@mui/icons-material";
import { IProdutoPedido } from "../../../../interface/IProdutoPedido";
import { TipoProduto } from "../../../../../produtos/enum/TipoProduto";
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import { contorno, cores, limitarLinhas, raio, superficie, texto } from "../../identidade";

interface ChatPdVCarrinhoProps {
    produtos: IProdutoPedido[];
    onAumentarQuantidade: (uuid: string) => Promise<void>;
    onDiminuirQuantidade: (uuid: string) => Promise<void>;
    onRemoverProduto: (uuid: string) => Promise<void>;
    onEditarProduto: (produto: IProdutoPedido) => void;
}

export const ChatPdVCarrinho = ({
    produtos,
    onAumentarQuantidade,
    onDiminuirQuantidade,
    onRemoverProduto,
    onEditarProduto,
}: ChatPdVCarrinhoProps) => {
    const { real } = useMoeda();

    if (produtos.length === 0) {
        return (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <Typography sx={{ ...texto.corpo, color: 'text.secondary' }}>
                    Comece o pedido aqui enquanto conversa.
                </Typography>
            </Box>
        );
    }

    const getPrecoTotal = (produto: IProdutoPedido) => {
        let totalProduto = produto.preco ?? 0;

        produto.itens?.forEach((item: any) => {
            totalProduto = totalProduto + ((item.preco ?? 0) * (item.quantidade ?? 1));
        });

        const quantidade = produto.quantidade ?? 1;
        return totalProduto * quantidade;
    };

    return (
        <>
            {produtos.map((produto) => (
                <Box
                    key={produto.uuid}
                    sx={{
                        px: 1.5,
                        py: 1.5,
                        borderRadius: raio.md,
                        border: theme => contorno(theme, 0.08),
                        backgroundColor: theme => superficie(theme, 0.03),
                    }}
                >
                    {/* Nome + Preço total */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.15, mb: 1.5 }}>
                        {/* Badge de quantidade, como nos itens do pedido no site. */}
                        <Box
                            sx={{
                                width: 22,
                                height: 22,
                                flexShrink: 0,
                                mt: '1px',
                                borderRadius: '6px',
                                display: 'grid',
                                placeItems: 'center',
                                fontSize: '11px',
                                fontWeight: 800,
                                color: 'text.secondary',
                                backgroundColor: theme => superficie(theme, 0.07),
                            }}
                        >
                            {produto.quantidade ?? 1}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ ...texto.titulo, lineHeight: 1.35, ...limitarLinhas(2) }}>
                                {produto.descricao}
                            </Typography>
                            <Typography sx={{ ...texto.apoio, color: 'text.secondary', mt: 0.35 }}>
                                {real(produto.preco ?? 0)} {produto.quantidade && produto.quantidade > 1 ? `x ${produto.quantidade}` : ''}
                            </Typography>
                            {produto.itens && produto.itens.length > 0 && (
                                <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.15 }}>
                                    {produto.itens.map((item: any, idx: number) => (
                                        <Typography
                                            key={idx}
                                            sx={{ ...texto.apoio, color: 'text.secondary', ...limitarLinhas(2) }}
                                        >
                                            + {item.descricao} {item.quantidade && item.quantidade > 1 ? `x ${item.quantidade}` : ''} {item.preco ? `(${real(item.preco * (item.quantidade ?? 1))})` : ''}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                            {produto.observacao && (
                                <Typography
                                    sx={{ ...texto.apoio, color: 'text.secondary', fontStyle: 'italic', mt: 0.35, ...limitarLinhas(3) }}
                                >
                                    Obs: {produto.observacao}
                                </Typography>
                            )}
                        </Box>

                        <Typography sx={{ fontSize: '13px', fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {real(getPrecoTotal(produto))}
                        </Typography>
                    </Box>

                    {/* Controles de quantidade + editar + excluir */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: raio.pill,
                                border: theme => contorno(theme, 0.09),
                                backgroundColor: theme => superficie(theme, 0.04),
                                p: 0.25,
                            }}
                        >
                            <ButtonBase
                                onClick={() => onDiminuirQuantidade(produto.uuid)}
                                aria-label="Diminuir quantidade"
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    color: cores.marca[500],
                                    '&:hover': { backgroundColor: alpha(cores.marca[500], 0.12) },
                                }}
                            >
                                <Remove sx={{ fontSize: 16 }} />
                            </ButtonBase>
                            <Typography sx={{ px: 1.25, fontSize: '12.5px', fontWeight: 800, minWidth: 22, textAlign: 'center' }}>
                                {produto.quantidade ?? 1}
                            </Typography>
                            <ButtonBase
                                onClick={() => onAumentarQuantidade(produto.uuid)}
                                aria-label="Aumentar quantidade"
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    color: cores.marca[500],
                                    '&:hover': { backgroundColor: alpha(cores.marca[500], 0.12) },
                                }}
                            >
                                <Add sx={{ fontSize: 16 }} />
                            </ButtonBase>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {(produto.tipo === TipoProduto.pizza || produto.tipo === TipoProduto.preparado) && (
                                <IconButton
                                    onClick={() => onEditarProduto(produto)}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: raio.sm,
                                        color: cores.marca[500],
                                        backgroundColor: alpha(cores.marca[500], 0.1),
                                        '&:hover': { backgroundColor: alpha(cores.marca[500], 0.18) },
                                    }}
                                >
                                    <EditOutlined sx={{ fontSize: 17 }} />
                                </IconButton>
                            )}
                            <IconButton
                                onClick={() => onRemoverProduto(produto.uuid)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: raio.sm,
                                    color: cores.sinal[500],
                                    backgroundColor: alpha(cores.sinal[500], 0.1),
                                    '&:hover': { backgroundColor: alpha(cores.sinal[500], 0.18) },
                                }}
                            >
                                <DeleteOutline sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>
            ))}
        </>
    );
};
